// Spec 39 (driver comms): the words the office screens use for a message
// thread and an assignment offer. Pure functions, so the load page's panel,
// the driver page's general thread, the inbox and the dispatch board all say
// the same thing about the same row, and each rule has one test.
//
// Times are rendered in the TENANT's zone (lib/orgTime.js), never the
// browser's: "Seen by Dana 10:42" has to agree with the stop times on the
// same page.

import moment from 'moment-timezone';
import { allowedForRole, currentRole } from './roles.js';

/** The composer's cap, the API's (messages.body is 1–2000 characters). */
export const MESSAGE_MAX_LENGTH = 2000;

/**
 * Who may message drivers: the API's DispatchRoles on every message route,
 * and the Messages rail entry's own gate (nav.vue reads this list). The
 * board's and the live map's "Message driver" ask the same question, so a
 * dispatcher is never offered a conversation the inbox would refuse them,
 * and a role the rail hides never sees a door into it.
 */
export const MESSAGING_ROLES = Object.freeze(['admin', 'dispatcher', 'accountant']);

/** Whether the signed-in user may message drivers. No store is nobody. */
export function canMessageDrivers(store) {
  return allowedForRole({ roles: MESSAGING_ROLES }, currentRole(store));
}

const QUICK_REPLY_LABELS = {
  loaded: 'Loaded',
  running_late: 'Running late',
  at_stop: 'At stop',
  departed: 'Departed',
};

const AUTHOR_KIND_LABELS = { driver: 'Driver', office: 'Dispatch', system: 'Ladeway' };

/** 'Driver' | 'Dispatch' | 'Ladeway' for a message's author.kind. */
export function authorKindLabel(kind) {
  return AUTHOR_KIND_LABELS[kind] || 'Dispatch';
}

/**
 * A message time: the clock alone when it is today in the tenant's zone,
 * the date and clock otherwise. '' for a missing or unreadable value, so a
 * row never reads "Invalid date".
 */
export function formatMessageTime(iso, zone, now = Date.now()) {
  if (!iso) return '';
  const at = moment(iso);
  if (!at.isValid()) return '';
  const local = at.tz(zone);
  const today = moment(now).tz(zone);
  return local.isSame(today, 'day') ? local.format('h:mm a') : local.format('M/D h:mm a');
}

/**
 * The chip a quick reply carries ("Loaded", "Running late (20 min)"), or
 * null for an ordinary typed message.
 */
export function quickReplyLabel(message) {
  const kind = message && message.quick_reply;
  if (!kind) return null;
  if (kind === 'running_late') {
    const minutes = Number(message.data && message.data.minutes);
    if (Number.isFinite(minutes) && minutes > 0) return `Running late (${minutes} min)`;
  }
  return QUICK_REPLY_LABELS[kind] || kind;
}

/**
 * What a quick reply did to the stop (spec 39 §6), as { label, tone }, or
 * null when it wrote nothing (Loaded, Running late, a typed message).
 *
 * The dedupe rule is the reason this exists on screen: a tap that found the
 * geofence's arrival already there wrote NOTHING, and the dispatcher reading
 * "At stop" next to a detention clock has to be able to see that.
 */
export function stopEventFact(message) {
  const event = message && message.data && message.data.stop_event;
  if (!event || !event.outcome) return null;
  const departed =
    event.kind === 'departed' ||
    event.kind === 'departure' ||
    (!event.kind && message.quick_reply === 'departed');
  const noun = departed ? 'Departure' : 'Arrival';
  if (event.outcome === 'recorded') return { label: `${noun} recorded`, tone: 'success' };
  if (event.outcome === 'already_recorded') {
    // The API names the geofence 'gps'; the office knows it as the geofence.
    const sourceLabel = { gps: 'geofence', dispatcher: 'office' }[event.source] || event.source;
    const by = sourceLabel ? ` (${sourceLabel})` : '';
    return { label: `${noun} already recorded${by}`, tone: 'info' };
  }
  if (event.outcome === 'refused') {
    return { label: `Not recorded: ${event.code || 'refused'}`, tone: 'warning' };
  }
  return null;
}

/**
 * The read receipt under a thread: for the newest office-authored message,
 * "Seen by <driver> <time>" when a driver's watermark has reached it, "Not
 * seen yet" when none has, and null when the office has not written
 * anything (there is nothing for a driver to have seen).
 *
 * A message m was read by u iff u's last_read_message_id >= m.id (spec 39
 * §3). A load reassigned mid-thread can have two driver readers; the one who
 * read most recently is named.
 */
export function readReceipt(messages, reads, zone, now = Date.now()) {
  const office = (messages || []).filter(
    m => m && m.id != null && m.author && m.author.kind === 'office'
  );
  if (!office.length) return null;
  const newestId = Math.max(...office.map(m => Number(m.id)));
  const seen = (reads || [])
    .filter(r => r && r.kind === 'driver' && Number(r.last_read_message_id) >= newestId)
    .sort((a, b) => Date.parse(b.read_at || 0) - Date.parse(a.read_at || 0));
  if (!seen.length) return 'Not seen yet';
  const reader = seen[0];
  const when = formatMessageTime(reader.read_at, zone, now);
  return `Seen by ${reader.name || 'the driver'}${when ? ` ${when}` : ''}`;
}

/**
 * The idempotency key a POST carries, so a retry of a send that did land
 * cannot store the message twice (client_uuid is unique per org). The
 * fallback is a v4 shape built from getRandomValues, for the older browsers
 * and test environments that have crypto but not randomUUID.
 */
export function newClientUuid() {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * The load page's offer line (spec 39 §7), as { value, label, text,
 * canReassign }, or null when there is nothing to say (no offer, or one that
 * was withdrawn by a reassignment). `value`/`label` are an LwChip on the
 * feedback axis; `text` is the sentence beside it.
 */
export function offerStatus(offer, zone, now = Date.now()) {
  if (!offer || !offer.status) return null;
  const who = offer.driver_name || 'the driver';
  switch (offer.status) {
    case 'pending': {
      const by = formatMessageTime(offer.respond_by, zone, now);
      return {
        value: 'info',
        label: 'Awaiting reply',
        text: by ? `Awaiting reply from ${who} (by ${by})` : `Awaiting reply from ${who}`,
        canReassign: false,
      };
    }
    case 'accepted': {
      const at = formatMessageTime(offer.responded_at, zone, now);
      return {
        value: 'success',
        label: 'Accepted',
        text: `Accepted by ${who}${at ? ` ${at}` : ''}`,
        canReassign: false,
      };
    }
    case 'declined':
      return {
        value: 'danger',
        label: 'Declined',
        text: offer.decline_reason
          ? `Declined by ${who}: ${offer.decline_reason}`
          : `Declined by ${who}`,
        canReassign: true,
      };
    case 'expired': {
      const by = formatMessageTime(offer.respond_by, zone, now);
      return {
        value: 'warning',
        label: 'No response',
        text: by ? `No response from ${who} by ${by}` : `No response from ${who}`,
        canReassign: true,
      };
    }
    default:
      return null;
  }
}

/**
 * The dispatch board's chip under the driver name, as { value, label,
 * title }, or null. The board read only sends an offer that is about the
 * driver on the load now, or a decline on a load with no driver (spec 39
 * §4), which is why a decline names the driver only in the second case:
 * there is no driver name above it to say who said no.
 */
export function boardOfferChip(offer, load, zone, now = Date.now()) {
  if (!offer || !offer.status) return null;
  switch (offer.status) {
    case 'pending': {
      const by = formatMessageTime(offer.respond_by, zone, now);
      return { value: 'info', label: 'Awaiting reply', title: by ? `Reply by ${by}` : null };
    }
    case 'accepted':
      return { value: 'success', label: 'Accepted', title: null };
    case 'declined': {
      const unassigned = !(load && load.driver);
      return {
        value: 'danger',
        label: unassigned && offer.driver_name ? `Declined by ${offer.driver_name}` : 'Declined',
        title: offer.decline_reason || null,
      };
    }
    case 'expired':
      return { value: 'warning', label: 'No response', title: null };
    default:
      return null;
  }
}

/** Whether an offer's state is one the office should reassign from. */
export function offerNeedsReassign(offer) {
  return !!offer && (offer.status === 'declined' || offer.status === 'expired');
}
